import { Controller } from "@hotwired/stimulus"

/**
 * Controller para el toggle de "En Ruta" en tickets.
 * Permite al mecánico incluir/excluir tickets de la ruta del día.
 * Maneja automáticamente cambios de estado cuando es necesario.
 */
export default class extends Controller {
  static targets = ["button", "toggle"]
  static values = {
    url: String,
    ticketId: Number
  }

  async toggle(event) {
    event.stopPropagation() // Prevent card zoom from triggering

    const button = this.buttonTarget
    const toggleSpan = this.toggleTarget
    const isCurrentlyOn = button.getAttribute('aria-checked') === 'true'
    const newValue = !isCurrentlyOn

    // Optimistic UI update
    this.updateUI(newValue)

    try {
      const response = await fetch(this.urlValue, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          ticket: { en_ruta: newValue }
        })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        console.log(`🚴 Ticket ${this.ticketIdValue} en_ruta: ${newValue}`)
        
        // Si el estado cambió, mostrar notificación y actualizar UI
        if (data.estado_cambiado) {
          this.showStateChangeNotification(data.estado_anterior, data.estado_nuevo)
          this.updateTicketState(data.estado_nuevo)
        }
        
        // Dispatch event to refresh route
        window.dispatchEvent(new CustomEvent('en-ruta-changed', {
          detail: {
            ticketId: this.ticketIdValue,
            enRuta: newValue,
            estadoCambiado: data.estado_cambiado,
            estadoNuevo: data.estado_nuevo
          }
        }))
      } else {
        // Revert on error
        console.error('Error updating en_ruta:', data.error || 'Unknown error')
        this.updateUI(!newValue)
        this.showError(data.error || 'No se pudo actualizar el estado')
      }
    } catch (error) {
      console.error('Error updating en_ruta:', error)
      this.updateUI(!newValue)
      this.showError('Error de conexión')
    }
  }

  updateUI(isOn) {
    const button = this.buttonTarget
    const toggleSpan = this.toggleTarget
    const container = this.element
    const label = container.querySelector('span')

    button.setAttribute('aria-checked', isOn)

    if (isOn) {
      button.classList.remove('bg-gray-200')
      button.classList.add('bg-gray-600')
      toggleSpan.classList.remove('translate-x-0')
      toggleSpan.classList.add('translate-x-3')
      if (label) label.classList.remove('line-through')
    } else {
      button.classList.remove('bg-gray-600')
      button.classList.add('bg-gray-200')
      toggleSpan.classList.remove('translate-x-3')
      toggleSpan.classList.add('translate-x-0')
      if (label) label.classList.add('line-through')
    }
  }

  showError(message = 'No se pudo actualizar') {
    const button = this.buttonTarget
    button.classList.add('ring-2', 'ring-red-500')
    setTimeout(() => {
      button.classList.remove('ring-2', 'ring-red-500')
    }, 1000)

    // Mostrar notificación de error
    const notification = document.createElement('div')
    notification.className = 'fixed bottom-4 right-4 bg-red-100 border border-red-400 text-red-800 px-4 py-3 rounded-lg shadow-lg z-50 max-w-sm'
    notification.innerHTML = `
      <div class="flex items-start gap-2">
        <svg class="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
        </svg>
        <span>${message}</span>
      </div>
    `
    document.body.appendChild(notification)
    
    // Remover después de 5 segundos
    setTimeout(() => {
      notification.style.opacity = '0'
      notification.style.transition = 'opacity 0.3s'
      setTimeout(() => notification.remove(), 300)
    }, 5000)
  }

  showStateChangeNotification(estadoAnterior, estadoNuevo) {
    // Mostrar notificación de cambio de estado
    const notification = document.createElement('div')
    notification.className = 'fixed bottom-4 right-4 bg-amber-100 border border-amber-400 text-amber-800 px-4 py-3 rounded-lg shadow-lg z-50 animate-fade-in'
    notification.innerHTML = `
      <div class="flex items-center gap-2">
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
        </svg>
        <span><strong>Estado cambiado:</strong> ${estadoAnterior} → ${estadoNuevo}</span>
      </div>
    `
    document.body.appendChild(notification)
    
    // Remover después de 4 segundos
    setTimeout(() => {
      notification.classList.add('animate-fade-out')
      setTimeout(() => notification.remove(), 300)
    }, 4000)
  }

  updateTicketState(nuevoEstado) {
    // Buscar el badge de estado en la tarjeta del ticket y actualizarlo
    const card = this.element.closest('[id^="ticket_"]')
    if (!card) return

    const estadoBadge = card.querySelector('[data-estado-badge]')
    const estadoText = card.querySelector('[data-estado-text]')
    
    if (estadoText) {
      estadoText.textContent = `⏱️ ${nuevoEstado}`
    }
    
    if (estadoBadge) {
      // Quitar clases de colores anteriores
      estadoBadge.classList.remove(
        'bg-green-50', 'text-green-700',
        'bg-red-50', 'text-red-700',
        'bg-blue-50', 'text-blue-700',
        'bg-yellow-50', 'text-yellow-700',
        'bg-gray-50', 'text-gray-700'
      )
      
      // Añadir colores según el nuevo estado
      const colorMap = {
        'Cerrado': ['bg-green-50', 'text-green-700'],
        'Nuevo': ['bg-red-50', 'text-red-700'],
        'Esperando al contacto': ['bg-blue-50', 'text-blue-700'],
        'Esperando acción por nuestra parte': ['bg-yellow-50', 'text-yellow-700']
      }
      
      const colors = colorMap[nuevoEstado] || ['bg-gray-50', 'text-gray-700']
      estadoBadge.classList.add(...colors)
    }
  }
}
